<template>
    <div class="container edit-container">
        <div class="row justify-content-center">
            <div class="col-12 case-settings-col-lg mt-5">
                <!-- Empty Message -->
                <div v-show="timers.length === 0 && !loading" class="text-center mt-4">
                    <h1 class="mb-3 font-weight-bolder h5">
                        <span class="icon icon-glyphicons-basic-stopwatch mr-1" style="margin-left: -5px"></span>
                        <span>Timers</span>
                    </h1>
                    <p class="mb-0 text-secondary">No timers have been added to this scenario yet.</p>
                    <br />
                    <button @click="openCreate()" :disabled="caseIsReadOnly" type="button" class="btn btn-primary">
                        Create a Timer
                    </button>
                </div>

                <!-- Non-Empty Display -->
                <div v-show="timers.length > 0 && !loading">
                    <div class="d-flex mb-4">
                        <div class="flex-grow-1 align-self-center">
                            <h1 class="mb-2 font-weight-bolder h5">
                                <span
                                    class="icon icon-glyphicons-basic-stopwatch mr-2"
                                    style="margin-left: -2px"
                                ></span>
                                <span>Timers</span>
                            </h1>
                            <p class="mb-0 text-secondary">Manage the timers in this scenario.</p>
                        </div>
                        <div class="flex-shrink-0 align-self-end">
                            <button
                                @click="openCreate()"
                                :disabled="caseIsReadOnly"
                                type="button"
                                class="btn btn-sm btn-primary ml-5"
                            >
                                Create a Timer
                            </button>
                        </div>
                    </div>
                </div>

                <!-- List Display -->
                <ul class="list-group">
                    <li v-for="timer in timers" :key="timer.id" class="list-group-item">
                        <div class="d-flex">
                            <div class="flex-grow-1 align-self-center">
                                <p class="mb-1">
                                    <span class="align-middle">{{ timer.label }}</span>
                                </p>
                                <div class="d-flex align-items-center mb-1">
                                    <div class="mr-2 pr-1">
                                        <span
                                            class="badge badge-gray align-middle"
                                        >
                                            {{ displayNodeLabel(timer.start_node_id) }}
                                        </span>
                                        <span v-if="timer.span_multiple_screens">
                                            <span class="icon icon-glyphicons-basic-arrow-right icon-xs mx-1"></span>
                                            <span class="badge badge-gray align-middle">
                                                {{ displayNodeLabel(timer.end_node_id) }}
                                            </span>
                                        </span>
                                    </div>
                                    <div v-show="timer.time_limit_seconds">
                                        <span class="small font-weight-bolder align-middle">
                                            <span class="icon icon-glyphicons-basic-stopwatch mr-1"></span>
                                            <span>{{ formatDuration(timer.time_limit_seconds) }}</span>
                                        </span>
                                    </div>
                                    <div v-if="!findNodeById(timer.start_node_id)" class="small align-middle ml-3">
                                        <span
                                            class="icon icon-glyphicons-basic-circle-alert text-danger align-middle mr-1"
                                        ></span>
                                        <span class="align-middle">Start screen missing</span>
                                    </div>
                                </div>
                                <p class="text-secondary small mb-0 mt-2">
                                    Last modified on {{ formatDate(timer.last_modified) }}
                                </p>
                            </div>
                            <div class="align-self-center">
                                <button
                                    @click="openDelete(timer)"
                                    :disabled="caseIsReadOnly"
                                    type="button"
                                    class="btn btn-light btn-sm mr-1"
                                >
                                    <span class="icon icon-glyphicons-basic-bin"></span>
                                </button>
                                <button @click="openEdit(timer)" type="button" class="btn btn-light btn-sm mr-1">
                                    <span class="icon icon-glyphicons-basic-square-edit"></span>
                                    Edit
                                </button>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Edit Modal -->
        <base-modal id="edit-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span v-show="selectedItem.id">Edit Timer</span>
                <span v-show="!selectedItem.id">Create Timer</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <base-form v-model="selectedItem" :fields="timerFields" ref="timerForm"></base-form>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button v-show="selectedItem.id" @click="update()" class="btn btn-primary" type="button">Save</button>
                <button v-show="!selectedItem.id" @click="create()" class="btn btn-primary" type="button">
                    Create
                </button>
            </template>
        </base-modal>

        <!-- Remove Modal -->
        <base-modal id="remove-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Remove Timer </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <p class="mb-0">Remove timer {{ selectedItem.label }}?</p>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="remove()" class="btn btn-danger" type="button">Remove</button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import http from "@/axios.js"
import Utilities from "@/assets/js/utilities.js"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"

export default {
    name: "CaseTimers",
    components: {
        BaseModal,
        BaseForm,
    },
    data() {
        return {
            timers: [],
            modalLoading: false,
            selectedItem: {},
        }
    },
    computed: {
        caseID() {
            return this.$route.params.id
        },
        loading() {
            return this.$store.getters.loading
        },
        caseIsReadOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        nodeOptions() {
            return this.$store.getters.nodeOptions
        },
        timerFields() {
            return [
                {
                    name: "Label",
                    key: "label",
                    subtext: "A label to identify this timer.",
                    maxlength: 250,
                    required: true,
                },
                {
                    name: "Show this label beside the timer",
                    key: "show_label",
                    type: "checkbox",
                    required: false,
                },
                {
                    name: "Timer Span",
                    key: "span_multiple_screens",
                    type: "radio",
                    options: [
                        {
                            label: "Single Screen",
                            value: false,
                        },
                        {
                            label: "Multiple Screens",
                            value: true,
                        },
                    ],
                },
                {
                    name: "Start Screen",
                    type: "search",
                    key: "start_node_id",
                    options: this.nodeOptions,
                    labelField: "label",
                    valueField: "id",
                    sortField: "number",
                    searchFields: ["label"],
                    required: true,
                    placeholder: "Select a screen",
                    subtext: "The timer will start when the learner reaches this screen.",
                },
                {
                    name: "End Screen",
                    type: "search",
                    key: "end_node_id",
                    options: this.nodeOptions,
                    labelField: "label",
                    valueField: "id",
                    sortField: "number",
                    searchFields: ["label"],
                    required(item) {
                        return Boolean(item.span_multiple_screens)
                    },
                    placeholder: "Select a screen",
                    subtext: "The timer will end on this screen.",
                    hidden(item) {
                        return !item.span_multiple_screens
                    },
                },
                {
                    name: "Timer Type",
                    type: "radio",
                    key: "display_type",
                    options: [
                        {
                            label: "Count Up",
                            value: "count_up",
                        },
                        {
                            label: "Count Down",
                            value: "count_down",
                        },
                        {
                            label: "Clock",
                            value: "clock",
                        },
                    ],
                },
                {
                    name: "Clock Start Time",
                    key: "clock_start_time",
                    type: "time",
                    hidden(item) {
                        return item.display_type !== "clock"
                    },
                    required(item) {
                        return item.display_type === "clock"
                    },
                },
                {
                    name: "Time Limit (seconds)",
                    key: "time_limit_seconds",
                    type: "number",
                    step: 1,
                    required(item) {
                        return item.display_type === "count_down"
                    },
                    subtext: "An optional limit for this timer. For countdown timers, this is the starting time.",
                },
                {
                    name: "Timeout Action",
                    type: "divider",
                    subtext: "Control what happens when the time limit is reached.",
                },
                {
                    name: "",
                    key: "action",
                    type: "radio",
                    options: [
                        {
                            label: "No action",
                            value: "none",
                        },
                        {
                            label: "Proceed directly to a screen",
                            value: "proceed_to_node",
                        },
                        {
                            label: "Proceed to a screen when clicked",
                            value: "proceed_when_clicked",
                        },
                    ],
                    required: false,
                },
                {
                    name: "Target Screen",
                    type: "search",
                    key: "target_node_id",
                    options: this.nodeOptions,
                    labelField: "label",
                    valueField: "id",
                    sortField: "number",
                    searchFields: ["label"],
                    required(item) {
                        return item.action !== "none"
                    },
                    placeholder: "Select a screen",
                    subtext: "The screen to take the learner to.",
                    hidden(item) {
                        return item.action == "none"
                    },
                },
                {
                    name: "Scoring",
                    type: "divider",
                },
                {
                    name: "Enable Scoring",
                    type: "checkbox",
                    key: "scoring_enabled",
                },
                {
                    name: "Deduct score if time exceeded",
                    type: "checkbox",
                    key: "deduct_score_after_limit",
                    hidden(item) {
                        return !item.scoring_enabled
                    },
                },
                {
                    name: "Score deducted per minute exceeded",
                    type: "number",
                    key: "points_deducted_per_minute",
                    hidden(item) {
                        return !item.scoring_enabled || !item.deduct_score_after_limit
                    },
                    placeholder: "Score",
                    unit: "per min",
                },
                {
                    name: "Maximum score deducted",
                    type: "number",
                    key: "points_deducted_maximum",
                    description: "The maximum score that can be deducted for exceeding this timer.",
                    hidden(item) {
                        return !item.scoring_enabled || !item.deduct_score_after_limit
                    },
                    placeholder: "Max score",
                },
                {
                    name: "Score awarded for completing before the limit",
                    type: "number",
                    key: "points_awarded_under_limit",
                    hidden(item) {
                        return !item.scoring_enabled
                    },
                    placeholder: "Score awarded",
                },
            ]
        },
    },
    methods: {
        formatDate(date) {
            return Utilities.formatDateTime(date)
        },
        formatDuration(seconds) {
            return Utilities.formatDurationFromSec(seconds)
        },
        findNodeById(nodeId) {
            return this.nodeOptions.find((node) => node.id == nodeId)
        },
        displayNodeLabel(nodeId) {
            if (!nodeId) {
                return null
            }
            let node = this.findNodeById(nodeId)
            if (node) {
                return `${node.number}. ${node.title}`
            } else {
                return null
            }
        },
        fetchData() {
            this.$store.dispatch("addLoad")
            let vm = this
            http.get("/api/cases/" + this.caseID + "/timers/")
                .then(function (response) {
                    vm.timers = response.data
                })
                .finally(() => vm.$store.dispatch("removeLoad"))
        },
        openCreate() {
            this.$refs.timerForm.resetValidate()
            this.selectedItem = {
                span_multiple_screens: false,
                display_type: "count_up",
                action: "none",
            }
            $("#edit-modal").modal("show")
        },
        openEdit(timer) {
            this.$refs.timerForm.resetValidate()
            this.selectedItem = Object.assign({}, timer)
            // The start or end node may have been removed by the user since the
            // timer was created, so set these fields to null to ensure that the input
            // shows as blank.
            let startNode = this.findNodeById(this.selectedItem.start_node_id)
            let endNode = this.findNodeById(this.selectedItem.end_node_id)
            if (!startNode) {
                this.selectedItem.start_node_id = null
            }
            if (!endNode) {
                this.selectedItem.end_node_id = null
            }
            $("#edit-modal").modal("show")
        },
        validateSelectedTimer() {
            if (this.selectedItem.clock_start_time == "") {
                this.selectedItem.clock_start_time = null
            }
            if (this.selectedItem.time_limit_seconds == "") {
                this.selectedItem.time_limit_seconds = null
            }
            if (this.selectedItem.points_deducted_maximum == "") {
                this.selectedItem.points_deducted_maximum = 0
            }
            if (this.selectedItem.points_deducted_per_minute == "") {
                this.selectedItem.points_deducted_per_minute = 0
            }
            if (this.selectedItem.points_awarded_under_limit == "") {
                this.selectedItem.points_awarded_under_limit = 0
            }
        },
        create() {
            if (!this.$refs.timerForm.validate()) {
                return
            }
            this.validateSelectedTimer()
            this.modalLoading = true
            let vm = this
            http.post("/api/cases/" + this.caseID + "/timers/", this.selectedItem)
                .then(function () {
                    vm.fetchData()
                    $("#edit-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Timer created.",
                        data: {},
                    })
                    vm.$refs.timerForm.resetValidate()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        update() {
            if (!this.$refs.timerForm.validate()) {
                return
            }
            this.validateSelectedTimer()
            this.modalLoading = true
            let vm = this
            http.put("/api/timers/" + this.selectedItem.id + "/", this.selectedItem)
                .then(function () {
                    vm.fetchData()
                    $("#edit-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Timer upated.",
                        data: {},
                    })
                    vm.$refs.timerForm.resetValidate()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        openDelete(timer) {
            this.selectedItem = timer
            $("#remove-modal").modal("show")
        },
        remove() {
            this.modalLoading = true
            let vm = this
            http.put("/api/timers/" + this.selectedItem.id + "/remove/")
                .then(function () {
                    vm.fetchData()
                    $("#remove-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Timer removed.",
                        data: {},
                    })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
    },
    created() {
        this.fetchData()
    },
}
</script>

<style lang="scss">
</style>